import { useReducer, useCallback, useMemo } from "react";

/**
 * Chat lifecycle phases.
 * @readonly
 * @enum {string}
 */
export const CHAT_PHASE = Object.freeze({
  IDLE: "idle",
  CONNECTING: "connecting",
  THINKING: "thinking",
  STREAMING: "streaming",
  DONE: "done",
  ERROR: "error",
});

/**
 * Valid transitions: current phase → allowed next phases.
 */
const VALID_TRANSITIONS = {
  [CHAT_PHASE.IDLE]: [CHAT_PHASE.CONNECTING],
  [CHAT_PHASE.CONNECTING]: [
    CHAT_PHASE.THINKING,
    CHAT_PHASE.ERROR,
    CHAT_PHASE.IDLE,
  ],
  [CHAT_PHASE.THINKING]: [
    CHAT_PHASE.STREAMING,
    CHAT_PHASE.DONE,
    CHAT_PHASE.ERROR,
    CHAT_PHASE.IDLE,
  ],
  [CHAT_PHASE.STREAMING]: [CHAT_PHASE.DONE, CHAT_PHASE.ERROR, CHAT_PHASE.IDLE],
  [CHAT_PHASE.DONE]: [CHAT_PHASE.CONNECTING, CHAT_PHASE.IDLE],
  [CHAT_PHASE.ERROR]: [CHAT_PHASE.CONNECTING, CHAT_PHASE.IDLE],
};

const ACTION = {
  TRANSITION: "TRANSITION",
  SET_ERROR: "SET_ERROR",
  RESET: "RESET",
};

const initialState = {
  phase: CHAT_PHASE.IDLE,
  error: null,
};

function reducer(state, action) {
  switch (action.type) {
    case ACTION.TRANSITION: {
      const { phase: nextPhase, error } = action.payload;
      const allowed = VALID_TRANSITIONS[state.phase];

      if (!allowed || !allowed.includes(nextPhase)) {
        if (process.env.NODE_ENV !== "production") {
          console.warn(
            `useAIChatPhase: invalid transition ${state.phase} → ${nextPhase}`
          );
        }
        return state;
      }

      return {
        phase: nextPhase,
        error: nextPhase === CHAT_PHASE.ERROR ? (error ?? state.error) : null,
      };
    }

    case ACTION.SET_ERROR:
      return { ...state, error: action.payload };

    case ACTION.RESET:
      return { ...initialState };

    default:
      return state;
  }
}

/**
 * State machine hook for AI chat lifecycle phases.
 *
 * Replaces the boolean-flag soup (`isSubmitting`, `isConnecting`,
 * `isThinking`, `complete`) with a single enum phase and validated
 * transitions.
 *
 * @returns {{
 *   chatPhase: string,
 *   error: any,
 *   transition: (nextPhase: string, error?: any) => void,
 *   reset: () => void,
 *   setError: (error: any) => void,
 *   derived: {
 *     isComplete: boolean,
 *     isSubmitting: boolean,
 *   },
 * }}
 */
export function useAIChatPhase() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const transition = useCallback((nextPhase, error = null) => {
    dispatch({ type: ACTION.TRANSITION, payload: { phase: nextPhase, error } });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: ACTION.RESET });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: ACTION.SET_ERROR, payload: error });
  }, []);

  const derived = useMemo(() => {
    const { phase } = state;
    const isComplete =
      phase === CHAT_PHASE.IDLE ||
      phase === CHAT_PHASE.DONE ||
      phase === CHAT_PHASE.ERROR;
    return {
      /** The chat is idle, done, or errored — no work in progress. */
      isComplete,
      /** A request is in flight (connecting/thinking/streaming). */
      isSubmitting: !isComplete,
    };
  }, [state]);

  return {
    chatPhase: state.phase,
    error: state.error,
    transition,
    reset,
    setError,
    derived,
  };
}
